import { clsx } from 'clsx';
import { debounce } from 'lodash-es';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SALE_BULK_UPDATE_FULFILLER_UPDATE_KEY } from 'src/components/Purchases/PurchaseActionDropdown';
import { SellerAccountEmployeeSelector } from 'src/components/Selectors/SellerAccountEmployeeSelector';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { ContentPlaceholder } from 'src/core/POS/ContentPlaceholder';
import { PosDropdown, PosDropdownItem } from 'src/core/POS/PosDropdown';
import { PosSearchBox } from 'src/core/POS/PosSearchBox/PosSearchBox';
import { vars } from 'src/core/themes';
import { Colors } from 'src/core/themes/constants/colors';
import { typography } from 'src/core/themes/shared.css';
import { Button } from 'src/core/ui';
import { PillList } from 'src/core/ui/PillList';
import * as pillListStyles from 'src/core/ui/PillList/PillList.css';
import { BulkUpdateSalesFulfillerDialog } from 'src/dialogs/BulkEdits/BulkUpdateSalesFulfillerDialog';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'src/modals/Modal';
import { LocationOutlineIcon } from 'src/svgs/Viagogo';
import { GeoLocationIcon } from 'src/svgs/Viagogo/functional-svgs/GeoLocation';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  BulkEditPreviewWithDetails,
  BulkEditSaleClient,
  CatalogClient,
  SellerAccountFulfillmentRulesClient,
  SellerAccountFulfillmentRulesInput,
  Venue,
} from 'src/WebApiController';

import * as styles from './FulfillmentRuleOverrideModal.css';

interface BulkCreateFulfillmentRulesResult {
  success: number[];
  duplicate: number[];
  error: number[];
}

interface InitialVenue {
  id: string;
  name: string;
  ruleId?: number;
}

interface FulfillmentRuleOverrideModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateSuccess?: () => void;
  initialProviderId?: string | null;
  initialVenues?: InitialVenue[];
}

// EMPTY_SELLER_USER_ID for SellerAccountEmployeeSelector
const EMPTY_SELLER_USER_ID = '';

// Venue search is now handled by CatalogClient

const mockPerformers = [
  { id: '1', name: 'Wicked' },
  { id: '2', name: 'Hamilton' },
  { id: '3', name: 'The Lion King' },
];

export const FulfillmentRuleOverrideModal: React.FC<
  FulfillmentRuleOverrideModalProps
> = ({
  isOpen,
  onClose,
  onCreateSuccess,
  initialProviderId,
  initialVenues,
}) => {
  const [title, setTitle] = useState('');
  const [fulfillmentProvider, setFulfillmentProvider] = useState<string | null>(
    null
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [venueResults, setVenueResults] = useState<Venue[]>([]);
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();
  const [selectedVenues, setSelectedVenues] = useState<string[]>([]);
  // Store full venue objects for selected venues
  const [selectedVenueData, setSelectedVenueData] = useState<
    Array<{ id: string; name: string; location: string }>
  >([]);
  const [existingRules, setExistingRules] = useState<Record<string, number>>(
    {}
  );
  const [isCreating, setIsCreating] = useState(false);

  const searchPlaceholder = useContent(ContentId.SearchVenuesPerformersVendors);
  const addFulfillerPlaceholder = useContent(ContentId.AddYourFulfiller);
  const duplicateVenueRuleMessage = useContent(ContentId.DuplicateVenueRule);

  const handleBulkCreateResults = async (
    result: BulkCreateFulfillmentRulesResult
  ) => {
    const duplicateVenueIds = result.duplicate || [];
    const errorVenueIds = result.error || [];
    const successVenueIds = result.success || [];

    if (duplicateVenueIds.length > 0) {
      // Show specific error message for duplicate venues
      const venueNames = duplicateVenueIds
        .map((venueId) => {
          const venueName =
            selectedVenueData.find((v) => v.id === venueId.toString())?.name ||
            venueId.toString();
          return venueName;
        })
        .join(', ');

      showErrorDialog(
        'createFulfillmentRuleForVenues',
        {
          message: `${duplicateVenueRuleMessage} (${venueNames})`,
        },
        {
          trackErrorData: {
            fulfillmentProvider,
            selectedVenues,
            duplicateVenues: duplicateVenueIds,
            successCount: successVenueIds.length,
            totalCount: selectedVenues.length,
          },
        }
      );
    }

    if (errorVenueIds.length > 0) {
      // Show generic error for other types of errors
      const errorVenueNames = errorVenueIds
        .map((venueId) => {
          const venueName =
            selectedVenueData.find((v) => v.id === venueId.toString())?.name ||
            venueId.toString();
          return venueName;
        })
        .join(', ');

      showErrorDialog(
        'createFulfillmentRuleForVenues',
        {
          message: `Failed to create fulfillment rules for venues: ${errorVenueNames}`,
        },
        {
          trackErrorData: {
            fulfillmentProvider,
            selectedVenues,
            errorVenues: errorVenueIds,
            successCount: successVenueIds.length,
            totalCount: selectedVenues.length,
          },
        }
      );
    }

    // If some succeeded, show a success message too
    if (successVenueIds.length > 0) {
      console.log(
        `Successfully created ${successVenueIds.length} out of ${selectedVenues.length} fulfillment rules`
      );
    }
  };

  const updateKey = SALE_BULK_UPDATE_FULFILLER_UPDATE_KEY;
  const { openDialog, closeDialog } = useDialog(
    DialogId.BulkBackfillSalesFulfiller,
    BulkUpdateSalesFulfillerDialog
  );

  const [searchQueryDebounced, setSearchQueryDebounced] = useState<
    string | undefined
  >();

  // Implement debounce exactly like EventSearch component
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onSearchTextChangedDebounced = useCallback(
    debounce((s: string | undefined) => {
      s = s?.trim();
      if (s !== searchQueryDebounced) {
        setSearchQueryDebounced(s);
      }
    }, 300),
    [] // Empty dependency array ensures this is only created once
  );

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    onSearchTextChangedDebounced(value);
  };

  const handleReset = useCallback(() => {
    setTitle('');
    setFulfillmentProvider(null);
    setSearchQuery('');
    setSearchQueryDebounced(undefined);
    setVenueResults([]);
    setSelectedVenues([]);
    setSelectedVenueData([]);
    setExistingRules({});
    setIsCreating(false);
  }, []);

  // Reset modal state when it opens
  useEffect(() => {
    if (isOpen) {
      handleReset();
      if (initialProviderId) {
        setFulfillmentProvider(initialProviderId);
      }
      if (initialVenues && initialVenues.length) {
        setSelectedVenues(initialVenues.map((v) => v.id));
        setSelectedVenueData(
          initialVenues.map((v) => ({ id: v.id, name: v.name, location: '' }))
        );
        setExistingRules(
          initialVenues.reduce<Record<string, number>>((acc, v) => {
            if (v.ruleId !== undefined) acc[v.id] = v.ruleId;
            return acc;
          }, {})
        );
      }
    }
  }, [isOpen, handleReset, initialProviderId, initialVenues]);

  useEffect(() => {
    const searchVenues = async () => {
      if (!searchQueryDebounced) {
        setVenueResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const results = await new CatalogClient(
          activeAccountWebClientConfig
        ).searchVenues(searchQueryDebounced);
        setVenueResults(results);
      } catch (error) {
        showErrorDialog(
          'searchVenues',
          { message: 'Error searching for venues' },
          {
            trackErrorData: { searchQuery: searchQueryDebounced },
          }
        );
      } finally {
        setIsSearching(false);
      }
    };

    searchVenues();
  }, [searchQueryDebounced, activeAccountWebClientConfig, showErrorDialog]);

  // No cleanup needed for lodash debounce

  const handleSelectVenue = useCallback(
    (venueId: number | null, venue?: Venue) => {
      if (venueId === null || !venue) return;
      const venueIdString = String(venueId);
      if (!selectedVenues.includes(venueIdString)) {
        // Store the full venue data for display
        const venueData = {
          id: venueIdString,
          name: venue.name,
          location: `${venue.city || ''}, ${venue.state || ''} ${venue.ctryCode || ''}`,
        };

        // Update both states together to prevent race conditions
        setSelectedVenues([...selectedVenues, venueIdString]);
        setSelectedVenueData([...selectedVenueData, venueData]);

        // Clear the search input, search results, and close the dropdown
        setSearchQuery('');
        setSearchQueryDebounced(undefined);
        setVenueResults([]);
      }
    },
    [selectedVenueData, selectedVenues]
  );

  const handleRemoveVenue = (venueId: string) => {
    if (!venueId || !selectedVenues.includes(venueId)) return;

    setSelectedVenues(selectedVenues.filter((id) => id !== venueId));
    setSelectedVenueData(
      selectedVenueData.filter((venue) => venue.id !== venueId)
    );
  };

  // Check if all required fields are filled to enable Create button
  const isFormValid = useMemo(() => {
    return !!fulfillmentProvider && selectedVenues.length > 0;
  }, [fulfillmentProvider, selectedVenues]);

  const onBulkUpdate = useCallback(
    async (
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails
    ) => {
      if (!fulfillmentProvider) {
        return;
      }

      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const preview = await new BulkEditSaleClient(
              activeAccountWebClientConfig
            ).bulkBackfillSalesFulfillerPreview({
              eventId: null,
              venueIds: selectedVenues.map((id) => parseInt(id, 10)),
              fulfillerSellerUserId: fulfillmentProvider,
            });

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkBackfillSalesFulfillerPreview', error, {
              trackErrorData: { fulfillmentProvider, selectedVenues },
            });
          }
        );
      } else {
        await tryInvokeApi(
          async () => {
            const succeeded = await new BulkEditSaleClient(
              activeAccountWebClientConfig
            ).bulkBackfillSalesFulfiller(
              {
                item1: preview!.preview,
                item2: fulfillmentProvider,
              },
              updateKey,
              supportBackgroundProcess
            );
            if (!supportBackgroundProcess) {
              closeDialog();
            }
          },
          (error) => {
            showErrorDialog('bulkBackfillSalesFulfiller', error, {
              trackErrorData: {
                preview,
              },
            });
          }
        );
      }
    },
    [
      activeAccountWebClientConfig,
      closeDialog,
      showErrorDialog,
      updateKey,
      fulfillmentProvider,
      selectedVenues,
    ]
  );

  const handleSaveWithBulkUpdate = () => {
    if (!fulfillmentProvider || selectedVenues.length === 0) {
      return;
    }

    openDialog({
      updateKey: updateKey,
      entityType: ActionOutboxEntityType.Sale,
      onSaveOverrideSettings: handleCreate,
      onBulkUpdate: onBulkUpdate,
      onCancel: () => {
        closeDialog();
      },
    });
  };

  const handleCreate = async () => {
    if (!fulfillmentProvider || selectedVenues.length === 0) {
      return;
    }

    setIsCreating(true);

    try {
      const client = new SellerAccountFulfillmentRulesClient(
        activeAccountWebClientConfig
      );

      const originalVenueIds = Object.keys(existingRules);
      const providerChanged =
        initialProviderId && fulfillmentProvider !== initialProviderId;

      const idsToDelete: number[] = [];
      if (providerChanged) {
        idsToDelete.push(...Object.values(existingRules));
      } else {
        idsToDelete.push(
          ...originalVenueIds
            .filter((id) => !selectedVenues.includes(id))
            .map((id) => existingRules[id])
        );
      }

      if (idsToDelete.length) {
        await client.deleteFulfillmentRules(idsToDelete);
      }

      const venuesToCreate = providerChanged
        ? selectedVenues
        : selectedVenues.filter((id) => !originalVenueIds.includes(id));

      if (venuesToCreate.length) {
        const fulfillmentRulesInput: SellerAccountFulfillmentRulesInput[] =
          venuesToCreate.map((venueId) => ({
            sellerAccountFulfillmentRulesId: null,
            fulfillerSellerUserId: fulfillmentProvider,
            fulfillmentRuleTargetTypeId: 3,
            viagogoEventId: null,
            eventMappingId: null,
            purchaseOrderVendorId: null,
            purchaseOrderId: null,
            viagogoVenueId: parseInt(venueId, 10),
          }));

        await tryInvokeApi(
          async () => {
            const result = await client.createFulfillmentRulesForVenues(
              fulfillmentRulesInput
            );

            if (result.data) {
              const resultText = await result.data.text();
              const parsedResult = JSON.parse(
                resultText
              ) as BulkCreateFulfillmentRulesResult;

              // Handle the result - check for duplicates or errors
              if (
                parsedResult.duplicate.length > 0 ||
                parsedResult.error.length > 0
              ) {
                await handleBulkCreateResults(parsedResult);
                return; // Don't close the modal if there were errors
              }
            }
          },
          (error) => {
            showErrorDialog('createFulfillmentRuleForVenues', error, {
              trackErrorData: { fulfillmentProvider, selectedVenues },
            });
          }
        );
      }

      // Wait for the success callback to complete before closing
      await onCreateSuccess?.();
      onClose();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('Error creating fulfillment rules:', error);

      // Try to parse the error message in case it contains the BulkCreateFulfillmentRulesResult
      try {
        if (error.message && error.message.includes('HTTP')) {
          const errorJsonMatch = error.message.match(/HTTP \d+: (.+)/);
          if (errorJsonMatch) {
            const errorData = JSON.parse(
              errorJsonMatch[1]
            ) as BulkCreateFulfillmentRulesResult;
            await handleBulkCreateResults(errorData);
            return;
          }
        }
      } catch (parseError) {
        console.error('Failed to parse error response:', parseError);
      }

      // Fallback to generic error message
      showErrorDialog(
        'createFulfillmentRuleForVenues',
        {
          message: 'Failed to create fulfillment rule for venues',
        },
        {
          trackErrorData: { fulfillmentProvider, selectedVenues },
        }
      );
    } finally {
      setIsCreating(false);
    }
  };

  // Venue search result item component
  const VenueSearchResultItem = ({ venue }: { venue: Venue }) => {
    return (
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            fontWeight: 500,
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          }}
        >
          {venue.name}
        </div>
        <div
          style={{
            marginLeft: vars.spacing.sm,
            fontStyle: 'italic',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            color: Colors.gray600,
          }}
        >
          {`${venue.city || ''}, ${venue.state || ''} ${venue.ctryCode || ''}`}
        </div>
      </div>
    );
  };

  // Prepare venue dropdown items
  const venueDropdownItems = useMemo(() => {
    if (isSearching) {
      return [
        <PosDropdownItem key="loading-1" disabled>
          <ContentPlaceholder height="24px" width="100%" />
        </PosDropdownItem>,
        <PosDropdownItem key="loading-2" disabled>
          <ContentPlaceholder height="24px" width="100%" />
        </PosDropdownItem>,
        <PosDropdownItem key="loading-3" disabled>
          <ContentPlaceholder height="24px" width="100%" />
        </PosDropdownItem>,
      ];
    }

    if (!venueResults.length && searchQueryDebounced && !isSearching) {
      return [
        <PosDropdownItem key="no-results" disabled>
          <div style={{ padding: vars.spacing.sm, textAlign: 'center' }}>
            <Content id={ContentId.NoVenuesFound} />
          </div>
        </PosDropdownItem>,
      ];
    }

    if (!venueResults.length) {
      return [];
    }

    // Header for venues section
    const items = [
      <PosDropdownItem key="venue-header">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: `${vars.spacing.xs} ${vars.spacing.sm}`,
            backgroundColor: 'transparent', // Removed purple background
            fontWeight: 600,
            fontSize: vars.typography.fontSize.sm,
            width: '100%',
          }}
        >
          <LocationOutlineIcon size={vars.iconSize.s} />
          <span style={{ marginLeft: vars.spacing.xs }}></span>
          <Content id={ContentId.Venues} />
        </div>
      </PosDropdownItem>,
    ];

    // Add venue items
    venueResults.forEach((venue, index) => {
      if (!venue || venue.viagId === null || venue.viagId === undefined) return;

      items.push(
        <PosDropdownItem
          key={`venue-${venue.viagId}-${index}`}
          title={venue.name}
          onClick={() => handleSelectVenue(venue.viagId, venue)}
        >
          <div
            style={{
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              width: '100%',
            }}
          >
            <VenueSearchResultItem venue={venue} />
          </div>
        </PosDropdownItem>
      );
    });

    return items;
  }, [handleSelectVenue, isSearching, searchQueryDebounced, venueResults]);

  const renderVenueSearch = () => {
    // Use the stored venue data directly instead of trying to find them in search results
    const selectedVenueObjects = selectedVenueData;

    // Show dropdown when typing or when we have results
    const showDropdown =
      searchQuery || (venueResults.length > 0 && searchQueryDebounced);

    return (
      <div>
        <div className={styles.fullWidthSearchContainer}>
          <div
            className={clsx(
              styles.searchBoxWrapper,
              styles.dropdownTriggerButton
            )}
          >
            <PosDropdown
              align="start"
              rootProps={{
                open: Boolean(showDropdown),
              }}
              trigger={
                <div style={{ width: '100%', display: 'block' }}>
                  <PosSearchBox
                    placeholder={searchPlaceholder}
                    onSearchChange={handleSearch}
                    value={searchQuery}
                    rootProps={{
                      style: {
                        width: '100%',
                        minWidth: '100%',
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                      },
                      variant: 'regular',
                      shape: 'square',
                    }}
                  />
                </div>
              }
              onOpenAutoFocus={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onCloseAutoFocus={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              {showDropdown ? venueDropdownItems : undefined}
            </PosDropdown>
          </div>

          {selectedVenues.length > 0 && (
            <div
              style={{
                marginTop: vars.spacing.m,
                marginBottom: vars.spacing.m,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: vars.spacing.m,
                  marginBottom: vars.spacing.xs,
                }}
              >
                <div
                  className={pillListStyles.pillListWrapperFullWidth}
                  style={{ flex: 1 }}
                >
                  <PillList
                    pills={selectedVenueObjects
                      .filter((venue) => venue && venue.id && venue.name)
                      .map((venue) => ({
                        value: venue.id,
                        display: venue.name,
                        prefixIcon: <GeoLocationIcon size={vars.iconSize.s} />,
                      }))}
                    onPillDeleted={(venueId) => handleRemoveVenue(venueId)}
                    flexWrap="wrap"
                    direction="row"
                  />
                </div>
                <button
                  onClick={() => {
                    setSelectedVenues([]);
                    setSelectedVenueData([]);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: vars.color.textPrimary,
                    fontSize: vars.typography.fontSize.base,
                    cursor: 'pointer',
                    padding: 0,
                    whiteSpace: 'nowrap',
                    textDecoration: 'none',
                    fontFamily: 'inherit',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.textDecoration = 'underline';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.textDecoration = 'none';
                  }}
                >
                  <Content id={ContentId.ClearAll} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalHeader onClose={onClose}>
        <div className={styles.headerBar}>
          <h5 className={clsx(typography.title5, styles.headerText)}>
            <Content id={ContentId.AddFulfillmentOverride} />
          </h5>
        </div>
      </ModalHeader>
      <ModalBody>
        <div style={{ padding: vars.spacing.lg }}>
          <div style={{ marginBottom: vars.spacing.lg }}>
            <SellerAccountEmployeeSelector
              value={fulfillmentProvider ?? undefined}
              disabled={false}
              allowUnchanged={false}
              onChange={(newId) => {
                setFulfillmentProvider(newId);
              }}
              onClick={(e) => e.stopPropagation()}
              isFulfillmentSection={true}
              placeholderText={addFulfillerPlaceholder}
            />
          </div>

          <div style={{ marginBottom: vars.spacing.lg }}>
            {renderVenueSearch()}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Button variant="text" onClick={handleReset}>
            <Content id={ContentId.Reset} />
          </Button>
          <div style={{ display: 'flex', gap: '12px' }}>
            <Button
              variant="regular"
              onClick={handleSaveWithBulkUpdate}
              disabled={!isFormValid || isCreating}
            >
              {isCreating ? (
                <Content id={ContentId.Creating} />
              ) : (
                <Content id={ContentId.Save} />
              )}
            </Button>
          </div>
        </div>
      </ModalFooter>
    </Modal>
  );
};
